import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Image,
  Pressable,
  ScrollView,
  Platform,
} from 'react-native';
import Modal from 'react-native-modal';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AntDesign, Feather } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { createZoneSheetVisibleAtom } from '../atoms';
import { insertGroup } from '../services/groupService';
import { uploadImage } from '../utils/firebaseImages';
import { ZONE_TYPE_INFO, ZONE_TOPICS, ZoneType, ZoneTopic } from '../types';
import PixelContainer from './PixelContainer';
import { fontStyle } from '../config/fonts';

export default function CreateZoneBottomSheet() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { user } = useAuth();
  const { requireAuth } = useRequireAuth();
  const queryClient = useQueryClient();
  const [isVisible, setIsVisible] = useAtom(createZoneSheetVisibleAtom);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageUri, setImageUri] = useState<string | null>(null);
  const [zoneType, setZoneType] = useState<ZoneType>('general');
  const [selectedTopics, setSelectedTopics] = useState<ZoneTopic[]>([]);

  const resetForm = () => {
    setName('');
    setDescription('');
    setImageUri(null);
    setZoneType('general');
    setSelectedTopics([]);
  };

  const handleClose = () => {
    resetForm();
    setIsVisible(false);
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not authenticated');

      let imageUrl: string | undefined;
      if (imageUri) {
        imageUrl = await uploadImage(imageUri);
      }

      return insertGroup(
        {
          name: name.trim(),
          description: description.trim() || undefined,
          image: imageUrl,
          type: zoneType,
          topics: selectedTopics,
        },
        user.uid
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      handleClose();
    },
    onError: (error) => {
      Alert.alert(t('common.error'), t('errors.loadFailed'));
      console.error('Zone creation error:', error);
    },
  });

  const handleCreate = () => {
    if (!requireAuth()) return;

    if (!name.trim()) {
      Alert.alert(t('common.error'), t('zone.zoneName'));
      return;
    }

    createMutation.mutate();
  };

  const handlePickImage = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8,
    });

    if (!result.canceled && result.assets[0]) {
      setImageUri(result.assets[0].uri);
    }
  };

  const handleRemoveImage = () => {
    setImageUri(null);
  };

  const toggleTopic = (topic: ZoneTopic) => {
    if (selectedTopics.includes(topic)) {
      setSelectedTopics(selectedTopics.filter((t) => t !== topic));
    } else if (selectedTopics.length < 5) {
      setSelectedTopics([...selectedTopics, topic]);
    }
  };

  return (
    <Modal
      isVisible={isVisible}
      onBackdropPress={handleClose}
      onSwipeComplete={handleClose}
      swipeDirection={['down']}
      style={styles.modal}
      backdropOpacity={0.5}
      animationIn="slideInUp"
      animationOut="slideOutDown"
      propagateSwipe
      customBackdrop={
        <Pressable 
          style={[
            styles.backdrop,
            Platform.OS === 'web' && {
              position: 'fixed' as any,
              width: '100vw' as any,
              height: '100vh' as any,
            }
          ]} 
          onPress={handleClose}
        />
      }
    >
      <View style={[styles.content, { backgroundColor: colors.background }]}>
        <View style={[styles.indicator, { backgroundColor: colors.border }]} />

        <Text style={[styles.title, fontStyle, { color: colors.text }]}>
          {t('zone.createZone')}
        </Text>

        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Zone Image Picker */}
          <Pressable onPress={handlePickImage} style={styles.imagePickerContainer}>
            {imageUri ? (
              <View style={styles.imagePreviewContainer}>
                <Image source={{ uri: imageUri }} style={styles.imagePreview} />
                <Pressable
                  onPress={handleRemoveImage}
                  style={[styles.removeImageButton, { backgroundColor: colors.error }]}
                >
                  <AntDesign name="close" size={14} color="#fff" />
                </Pressable>
              </View>
            ) : (
              <View style={[styles.imagePlaceholder, { backgroundColor: colors.surface }]}>
                <Feather name="camera" size={28} color={colors.textSecondary} />
              </View>
            )}
          </Pressable>

          {/* Zone Type Selection */}
          <View style={styles.sectionContainer}>
            <Text style={[styles.label, fontStyle, { color: colors.text }]}>
              {t('zone.selectType')}
            </Text>
            <View style={styles.typeGrid}>
              {(Object.keys(ZONE_TYPE_INFO) as ZoneType[]).map((type) => {
                const info = ZONE_TYPE_INFO[type];
                const isSelected = zoneType === type;

                return (
                  <Pressable
                    key={type}
                    style={[
                      styles.typeCard,
                      { borderColor: isSelected ? info.color : colors.border },
                      isSelected && { backgroundColor: `${info.color}15` },
                    ]}
                    onPress={() => setZoneType(type)}
                  >
                    <Text style={styles.typeEmoji}>{info.emoji}</Text>
                    <Text
                      style={[
                        styles.typeName,
                        fontStyle,
                        { color: isSelected ? info.color : colors.text },
                      ]}
                    >
                      {t(`zone.types.${type}`)}
                    </Text>
                    <Text
                      style={[styles.typeDescription, fontStyle, { color: colors.textSecondary }]}
                      numberOfLines={2}
                    >
                      {t(`zone.typeDescriptions.${type}`)}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          {/* Zone Name Input */}
          <View style={styles.inputContainer}>
            <Text style={[styles.label, fontStyle, { color: colors.text }]}>
              {t('zone.zoneName')}
            </Text>
            <TextInput
              style={[
                styles.input,
                fontStyle,
                {
                  backgroundColor: colors.surface,
                  color: colors.text,
                  borderColor: colors.border,
                },
              ]}
              placeholder={t('zone.zoneNamePlaceholder')}
              placeholderTextColor={colors.textSecondary}
              value={name}
              onChangeText={setName}
              maxLength={50}
            />
          </View>

          {/* Zone Description Input */}
          <View style={styles.inputContainer}>
            <Text style={[styles.label, fontStyle, { color: colors.text }]}>
              {t('zone.zoneDescription')}
            </Text>
            <TextInput
              style={[
                styles.input,
                styles.descriptionInput,
                fontStyle,
                {
                  backgroundColor: colors.surface,
                  color: colors.text,
                  borderColor: colors.border,
                },
              ]}
              placeholder={t('zone.zoneDescriptionPlaceholder')}
              placeholderTextColor={colors.textSecondary}
              value={description}
              onChangeText={setDescription}
              multiline
              numberOfLines={3}
              maxLength={200}
            />
          </View>

          {/* Topics Selection */}
          <View style={styles.sectionContainer}>
            <Text style={[styles.label, fontStyle, { color: colors.text }]}>
              {t('zone.selectTopics')} ({selectedTopics.length}/5)
            </Text>
            <View style={styles.topicsGrid}>
              {ZONE_TOPICS.map((topic) => {
                const isSelected = selectedTopics.includes(topic);

                return (
                  <Pressable
                    key={topic}
                    style={[
                      styles.topicChip,
                      { borderColor: colors.border },
                      isSelected && { backgroundColor: colors.primary, borderColor: colors.primary },
                    ]}
                    onPress={() => toggleTopic(topic)}
                  >
                    <Text
                      style={[
                        styles.topicChipText,
                        fontStyle,
                        { color: isSelected ? '#fff' : colors.text },
                      ]}
                    >
                      {t(`zone.topics.${topic}`)}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          {/* Create Button */}
          <Pressable onPress={handleCreate} disabled={!name.trim() || createMutation.isPending}>
            <PixelContainer
              backgroundColor={(!name.trim() || createMutation.isPending) ? colors.surface : colors.primary}
              cornerSize={4}
              style={[styles.createButton, (!name.trim() || createMutation.isPending) && styles.createButtonDisabled]}
            >
              <Text style={[styles.createButtonText, fontStyle]}>
                {createMutation.isPending ? t('zone.creating') : t('zone.create')}
              </Text>
            </PixelContainer>
          </Pressable>

          <View style={styles.bottomSpacer} />
        </ScrollView>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modal: {
    justifyContent: 'flex-end',
    margin: 0,
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  content: {
    maxHeight: '90%',
    paddingTop: 12,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
  },
  indicator: {
    width: 40,
    height: 4,
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 16,
    paddingHorizontal: 24,
  },
  scrollView: {
    paddingHorizontal: 24,
  },
  imagePickerContainer: {
    alignSelf: 'center',
    marginBottom: 20,
  },
  imagePlaceholder: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  imagePreviewContainer: {
    position: 'relative',
  },
  imagePreview: {
    width: 80,
    height: 80,
    borderRadius: 40,
  },
  removeImageButton: {
    position: 'absolute',
    top: -4,
    right: -4,
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sectionContainer: {
    marginBottom: 20,
  },
  inputContainer: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  input: {
    width: '100%',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 10,
    borderWidth: 1,
    fontSize: 15,
  },
  descriptionInput: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  typeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  typeCard: {
    width: '48%',
    padding: 12,
    borderRadius: 12,
    borderWidth: 2,
    alignItems: 'center',
  },
  typeEmoji: {
    fontSize: 24,
    marginBottom: 4,
  },
  typeName: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 2,
  },
  typeDescription: {
    fontSize: 11,
    textAlign: 'center',
    lineHeight: 14,
  },
  topicsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  topicChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
  },
  topicChipText: {
    fontSize: 13,
    fontWeight: '500',
  },
  createButton: {
    width: '100%',
    paddingVertical: 14,
    alignItems: 'center',
    marginTop: 8,
  },
  createButtonDisabled: {
    opacity: 0.6,
  },
  createButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  bottomSpacer: {
    height: 40,
  },
});
